import { Injectable, computed, signal } from '@angular/core';

/**
 * Tracks in-flight work that should block the whole screen with a full-page
 * loading overlay.
 *
 * The service keeps a counter of active "loading holders" rather than a plain
 * boolean so that concurrent sources (e.g. several HTTP requests fired by a
 * single page) all have to finish before the overlay is hidden. The overlay is
 * visible while the counter is greater than zero.
 *
 * It is fed automatically by the loading HTTP interceptor for every request,
 * and can also be driven manually via {@link show} / {@link hide} for work that
 * is not backed by an HTTP call.
 */
@Injectable({
  providedIn: 'root'
})
export class LoadingService {
  private readonly activeCount = signal(0);

  /** True while at least one loading holder is active. */
  readonly isLoading = computed(() => this.activeCount() > 0);

  /** Registers a new loading holder (increments the active counter). */
  show(): void {
    this.activeCount.update((count) => count + 1);
  }

  /**
   * Releases a loading holder (decrements the active counter). Guarded so the
   * counter can never drop below zero even if hide() is called more times than
   * show().
   */
  hide(): void {
    this.activeCount.update((count) => (count > 0 ? count - 1 : 0));
  }

  /** Force-clears every loading holder. Use sparingly (e.g. on hard resets). */
  reset(): void {
    this.activeCount.set(0);
  }
}
